import { Box, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { HomeWorkOutlined } from '@material-ui/icons';
import { insertIf, PURCHASE, REFINANCE } from '@roc/feature-utils';
import { observer } from 'mobx-react';
import {
  FORM_MAX_WIDTH,
  PROPERTY_TYPE_OTHER,
  propertySourceValues,
  propertyTypeOptions,
} from '../../utils/constants';
import {
  AnnualHOAProvidedAtOriginationValue,
  AnnualInsurance,
  AnnualTaxes,
  ArmsLengthComment,
  BorrowerKnowOwnerOfRecord,
  ConstructionRenoSpendToDate,
  County,
  CurrentlyOccupied,
  CurrentMarketValue,
  HasBeenRecentlyRenovated,
  HoldingCosts,
  MonthlyMarketRent,
  OutstandingLoanOnTheProperty,
  PayoffAmount,
  PropertyAddress,
  PropertyLeased,
  PropertyOwnership,
  PropertySourcing,
  PropertySourcingExplanation,
  PropertyType,
  PropertyTypeDescription,
  ProposedConstructionBudget,
  PurchaseDate,
  PurchasePrice,
  Section8,
  SeekingCashOut,
  SellerCredit,
  SellerCreditAmount,
  SoonToBeListedForSale,
  Units,
  WhatWasThePurchasePrice,
} from '../fixFlip/propertyInformationFields';
import {
  PropertyInformationTimeline,
  TimelineIconConstructionBudget,
  TimelineIconMarketValue,
  TimelineIconRenoSpent,
  TimelineIconPurchasePrice,
} from '../fixFlip/propertyInformationTimeline';
import { NetOperatingIncome } from 'libs/feature-loans/src/rentalPricer/pricerSteps/components/netOperatingIncome';
import { formatCurrency } from '@roc/ui/utils';
import { useEffect } from 'react';
import { RuralPropertyWarning } from 'libs/feature-loans/src/loanSubmission/components/ruralPropertyWarning';
import { buildAddress } from '../../utils/utils';
import { EditControls } from '../common/editControls';
import { RoundedBackgroundIcon } from '../common/roundedBackgroundIcon';
import { TitleDivider } from '../common/titleDivider';
import { WholesalerAlertMessage } from '../fixFlip/wholesalerAlertMessage';
import { WholesalerPropertyContractAmount } from '../fixFlip/propertyInformationFields';
import { ItemDisplay } from '../fixFlip/itemDisplay';
import { SinglePropertyRentalStore } from '../../stores/singlePropertyRentalStore';
import { SinglePropertyPropertyFormStore } from '../../stores/singleProperty/singlePropertyPropertiesStore';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    position: 'relative',
    maxWidth: 1300,
    margin: 'auto',
  },
  activeConnector: {
    height: '50px',
    backgroundColor: theme.palette.primary.main,
    width: '4px',
  },
  inactiveConnector: {
    height: '50px',
    width: '4px',
  },
  substractionContainer: {
    display: 'flex',
    width: '90%',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: '20px',
  },
  symbols: {
    color: '#BABEC5',
    fontSize: '60px',
    fontWeight: 300,
  },
  timelineDot: {},
}));
interface PropertyInformationFormProps {
  store: SinglePropertyPropertyFormStore;
  onDeleteClick: (propertyKey: string) => void;
  removeButtonDisabled?: boolean;
  showActions: boolean;
  quoteTypeStore: SinglePropertyRentalStore;
}

export const SinglePropertyRentalPropertyInformationForm = observer(
  ({
    store,
    onDeleteClick,
    showActions = true,
    removeButtonDisabled = false,
    quoteTypeStore,
  }: PropertyInformationFormProps) => {
    useEffect(() => {
      quoteTypeStore.propertiesStore.fetchStateCounties();
    }, [store.form.fields.state.value]);

    const classes = useStyles();
    const propertyKey = store.key;

    function saveHandler() {
      const isValid = store.validateAndCleanupProperty(
        'Please complete all the required fields'
      );
      if (isValid) {
        store.setEditMode(false);
      }
    }
    const counties = quoteTypeStore.propertiesStore.counties ?? [];

    const loanPurpose = store.form.fields.loanPurpose.value;

    const isPurchase = loanPurpose !== REFINANCE;
    const isRefinance = loanPurpose === REFINANCE;
    const isOtherPropertyType =
      store.form.fields.propertyType.value === PROPERTY_TYPE_OTHER;

    const propertySourcing = store.form.fields.propertySourcing.value;

    const displayWholesalerAlertMessage =
      isPurchase && propertySourcing === propertySourceValues.wholesaler;

    const showPropertySourcingExplanation =
      isPurchase && propertySourcing === propertySourceValues.other;

    const calculatedIncome =
      (Number(store?.form?.fields?.monthlyMarketRent?.value) ?? 0) * 12;
    const calculatedExpenses =
      (Number(store?.form?.fields?.annualHOAProvidedAtOrigination?.value) ??
        0) +
      (Number(store?.form?.fields?.annualTaxes?.value) ?? 0) +
      (Number(store?.form?.fields?.annualInsurance?.value) ?? 0);
    const calculatedOperatingIncome = calculatedIncome - calculatedExpenses;

    return (
      <>
        <Grid
          container
          spacing={2}
          className={classes.container}
          style={{ maxWidth: FORM_MAX_WIDTH, marginBottom: '0.5rem' }}
        >
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              justifyContent="flex-start"
              alignItems="center"
            >
              <Grid item>
                <RoundedBackgroundIcon
                  Icon={HomeWorkOutlined}
                  iconColor="#6D6D6D"
                />
              </Grid>
              <Grid item>
                <Typography variant="h4" style={{ color: '#6D6D6D' }}>
                  Add Property
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            style={{ marginTop: '1rem', marginBottom: '1rem' }}
          >
            <TitleDivider title="Property Details" />
          </Grid>
          <Grid item xs={12}>
            <PropertyAddress store={store} quoteTypeStore={quoteTypeStore} />
          </Grid>
          {store.form.fields.address.value && (
            <Grid item xs={12} sm={6}>
              <County store={store} counties={counties} />
            </Grid>
          )}
          {isOtherPropertyType && <Grid item xs={12} />}
          <Grid item xs={isOtherPropertyType ? 4 : 6}>
            <PropertyType store={store} options={propertyTypeOptions} />
          </Grid>
          {isOtherPropertyType && (
            <Grid item xs={4}>
              <PropertyTypeDescription store={store} />
            </Grid>
          )}
          {isOtherPropertyType && (
            <Grid item xs={isOtherPropertyType ? 4 : 6}>
              <Units store={store} />
            </Grid>
          )}

          <Grid item xs={12} />
          <Grid item xs={12} sm={6}>
            <PropertyOwnership store={store} />
          </Grid>
          {loanPurpose === PURCHASE && (
            <Grid item xs={12} sm={6}>
              <PropertyLeased store={store}></PropertyLeased>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Section8 store={store}></Section8>
          </Grid>

          {isRefinance && <RefinanceFields store={store} />}
        </Grid>
        <Grid
          container
          spacing={2}
          className={classes.container}
          justifyContent="center"
          style={{ maxWidth: FORM_MAX_WIDTH, paddingInline: 0 }}
        >
          <Grid
            item
            xs={12}
            style={{ marginTop: '1rem', marginBottom: '1rem' }}
          >
            <TitleDivider title="Project Details" />
          </Grid>
          {displayWholesalerAlertMessage && (
            <Grid item xs={12} style={{ paddingInline: '1rem' }}>
              <WholesalerPropertyContractAmount store={store} />
            </Grid>
          )}
          {showPropertySourcingExplanation && (
            <Grid item xs={12} style={{ paddingInline: '1rem' }}>
              <PropertySourcingExplanation store={store} />
            </Grid>
          )}
          <Grid item xs={12}>
            <PropertyInformationTimeline
              items={[
                {
                  icon: <TimelineIconPurchasePrice />,
                  ...(isPurchase
                    ? { right: <PurchasePrice store={store} /> }
                    : { left: <WhatWasThePurchasePrice store={store} /> }),
                },
                ...insertIf(!isPurchase, [
                  {
                    icon: <TimelineIconMarketValue />,
                    left: <ConstructionRenoSpendToDate store={store} />,
                  },
                ]),

                {
                  icon: <TimelineIconRenoSpent />,
                  right: <CurrentMarketValue store={store} />,
                },
                {
                  icon: <TimelineIconMarketValue />,
                  right: <MonthlyMarketRent store={store} />,
                  gray: true,
                },
                {
                  icon: <TimelineIconRenoSpent />,
                  right: <AnnualTaxes store={store} />,
                  gray: true,
                },
                {
                  icon: <TimelineIconMarketValue />,
                  right: <AnnualInsurance store={store} />,
                  gray: true,
                },
                {
                  icon: <TimelineIconMarketValue />,
                  right: (
                    <AnnualHOAProvidedAtOriginationValue
                      addMarkForRequiredFields={true}
                      isRequired={true}
                      standaloneLabel={false}
                      store={store}
                    />
                  ),
                  gray: true,
                },
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <Box className={classes.substractionContainer}>
              <ItemDisplay
                bottomLabel
                label="Total Annual Income"
                value={`${formatCurrency(calculatedIncome ?? 0)}`}
              ></ItemDisplay>
              <Typography
                className={classes.symbols}
                align="center"
                variant="h4"
              >
                -
              </Typography>
              <ItemDisplay
                bottomLabel
                label="Annual Expenses"
                value={`${formatCurrency(calculatedExpenses ?? 0)}`}
              ></ItemDisplay>
              <Typography
                className={classes.symbols}
                align="center"
                variant="h4"
              >
                =
              </Typography>
              <ItemDisplay
                bottomLabel
                label="Annual NOI"
                value={`${formatCurrency(calculatedOperatingIncome ?? 0)}`}
              ></ItemDisplay>
            </Box>
          </Grid>
        </Grid>
        {showActions && (
          <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
            style={{ padding: '1rem' }}
          >
            <EditControls
              onDeleteClick={() => onDeleteClick(propertyKey)}
              onSaveClick={saveHandler}
              removeButtonDisabled={removeButtonDisabled}
            />
          </Grid>
        )}
      </>
    );
  }
);

const RefinanceFields = observer(({ store }) => {
  const outstandingLoanOnTheProperty =
    store.form.fields.outstandingLoanOnTheProperty.value;

  return (
    <>
      <Grid item xs={12} sm={6}>
        <PurchaseDate store={store} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <OutstandingLoanOnTheProperty store={store} />
      </Grid>
      {outstandingLoanOnTheProperty && (
        <Grid item xs={12} sm={6}>
          <PayoffAmount store={store} />
        </Grid>
      )}
      <Grid xs={12} />
      {outstandingLoanOnTheProperty && (
        <Grid item xs={12} sm={6}>
          <SeekingCashOut store={store} />
        </Grid>
      )}
    </>
  );
});
